/* eslint-disable camelcase */
import React, { useEffect, useState } from "react";
import { headingObjectProp, wysiwygProp } from "core-web/utils/proptypes/modules/textProps";

import Flexible from "theme/components/contentBlocks/Flexible";
import PropTypes from "prop-types";
import activeSetting from "theme/libs/wordpress/utils/activeSetting";
import colors from "theme/config/colors";
import { mediaQueryProp } from "core-web/utils/proptypes/modules/mediaProps";
import parseProductSelection from "theme/libs/wordpress/utils/transformProductSelection";
import { textColorProp } from "core-web/utils/proptypes/modules/styleProps";
import transformAlgoliaProduct from "core-web/utils/dataTransformers/transformAlgoliaProduct";
import transformColor from "theme/libs/wordpress/utils/transformColor";
import transformFlexibleBackground from "theme/libs/wordpress/utils/transformFlexibleBackground";
import transformHeading from "theme/libs/wordpress/utils/transformHeading";
import transformParagraphFontSize from "theme/libs/wordpress/utils/transformParagraphFontSize";
import useAboveBreakpoint from "core-web/hooks/useAboveBreakpoint";
import useBelowBreakpoint from "core-web/hooks/useBelowBreakpoint";

const FlexibleBlock = ({
    layout,
    // data,
    // custom_column_order,
    numberOfColumns,
    content,
    isHero,

    isColumn,
    isFirstModule,
    mediaQuery,
    useGridWrapper,
    scrollToClassName,
}) => {
    const {
        settings,
        background: backgroundObject,
        // button,
        content_horizontal_alignment,
        content_vertical_alignment,
        // heading,
        paragraph,
        tag,
        text_color,

        background_flexible: backgroundFlexible,
        scroll_to_button: scrollToButton,
        cta_settings: ctaSettings,
        paragraph_font_sizes: paragraphFontSizes,
        is_manual_paragraph_font_size: isManualParagraphFontSize,
        play_button: playButton,
    } = content;

    const textColor = text_color === "default" ? colors.white : text_color;
    const isMobile = useBelowBreakpoint("tabletLg");

    const device = content.heading.customize_mobile && isMobile ? "mobile" : "desktop";

    const heading = content.heading.data?.[device]?.heading;

    const button = {
        theme: content.button.button_theme === "default" ? "white_solid" : content.button.button_theme,
        label: content.button.label,
        link: content.button.link,
    };

    // const { button_theme: scrollToTheme, label: scrollToLabel } = scrollToButton || {};
    const { label: playButtonLabel, button_theme: playButtonTheme } = playButton || {};

    // Logic for image and mediaQueries
    // Default is optimized for a image with 100vw
    const fullWidthQueries = {
        w: [540, 740, 992, 1200, 1500, 1880, 2400, 2600],
        q: 70,
    };
    const finalQueries = mediaQuery ?? fullWidthQueries;

    // Transform background data
    const transformedBackground = transformFlexibleBackground(backgroundObject);
    const mobileBackground = transformedBackground.mobile || transformedBackground.desktop;
    const desktopBackground = transformedBackground.desktop || transformedBackground.mobile;

    const isDesktop = useAboveBreakpoint("tabletSm");
    // const device = isDesktop ? "desktop" : "mobile";

    // Save background data
    const [background, setBackground] = useState(isDesktop ? desktopBackground : mobileBackground);

    // Update background and video object depending on device
    useEffect(() => {
        setBackground(isDesktop ? desktopBackground : mobileBackground);
    }, [backgroundFlexible, isDesktop]);

    //Text-styles used in Flexible module
    const textStyleThemes = {
        //If default textStyles is changed, it need to be changed in HoverOverlayContent.js as well.
        paragraph: ["Myriad Pro/18-140", null, null, null, "Myriad Pro/22-140"],
        paragraphTwoColumn: ["Myriad Pro/18-140", null, null, null, "Myriad Pro/22-140"],
        paragraphThreeColumn: ["Myriad Pro/18-140", null, null, null, "Myriad Pro/18-140"],
    };

    let paragraphTextStyle = textStyleThemes["paragraph"];

    if (numberOfColumns === "2") {
        paragraphTextStyle = textStyleThemes["paragraphTwoColumn"];
    } else if (numberOfColumns === "3") {
        paragraphTextStyle = textStyleThemes["paragraphThreeColumn"];
    }

    // const paragraphFontSize = transformParagraphFontSize(paragraphFontSizes, isManualParagraphFontSize);

    // if (paragraphFontSize) {
    //     paragraphTextStyle = paragraphFontSize;
    // }

    const hasButton = ctaSettings === "button";
    const hasScrollTo = ctaSettings === "scroll_to";

    // const buttonLabel = hasScrollTo ? scrollToLabel : hasButton ? simpleButtonLabel : null;
    // const buttonTheme = hasScrollTo ? scrollToTheme : hasButton ? simpleButtonTheme : null;

    const flexibleProps = {
        hasHover: isColumn && hasButton,
        background,
        backgroundType: background?.type,
        button: (activeSetting(settings, "link") && button) || undefined,
        heading:
            (activeSetting(settings, "heading") && transformHeading(heading, layout, numberOfColumns)) || undefined,
        isAboveTheFold: isFirstModule,
        isHero,
        mediaQuery: finalQueries,
        tag: (activeSetting(settings, "tag") && tag) || undefined,
        paragraph: (activeSetting(settings, "paragraph") && paragraph) || undefined,
        paragraphTextStyle,
        playButtonLabel: playButtonLabel || "",
        playButtonTheme: playButtonTheme || "",
        textColor,
        useGridWrapper,
        withBorderRadius: isColumn,
        xAlignContent: "left", //use content_horizontal_alignment & add logic?
        yAlignContent: "bottom", //use content_vertical_alignment & add logic?
    };

    return (
        <Flexible
            // buttonLink={hasButton && buttonLink ? buttonLink : null}
            hasScrollTo={hasScrollTo}
            playButtonLabel={playButtonLabel || ""}
            playButtonTheme={playButtonTheme || ""}
            scrollToClassName={scrollToClassName}
            {...flexibleProps}
        />
    );
};

FlexibleBlock.propTypes = {
    content: PropTypes.shape({
        background_flexible: PropTypes.exact({
            mobile: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.object])),
            desktop: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.object])),
        }).isRequired,
        button: PropTypes.shape({
            button_theme: PropTypes.string,
            link: PropTypes.string,
            label: PropTypes.string,
        }),
        cta_settings: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]).isRequired,
        heading: headingObjectProp,
        link: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.bool]),
        paragraph: wysiwygProp,
        play_button: PropTypes.object,
        scroll_to_button: PropTypes.object,
        settings: PropTypes.arrayOf(PropTypes.string).isRequired,
        tag: PropTypes.string,
        text_color: textColorProp,
    }),
    isColumn: PropTypes.bool,
    isFirstModule: PropTypes.bool,
    isHero: PropTypes.bool,
    isThirdItem: PropTypes.bool,
    layout: PropTypes.string,
    mediaQuery: mediaQueryProp,
    scrollToClassName: PropTypes.string,
    useGridWrapper: PropTypes.bool,
};

FlexibleBlock.defaultProps = {
    content: {
        tag: undefined,
        heading: {
            text: undefined,
            type: "h2",
        },
        link: undefined,
        paragraph: undefined,
        play_button_label: "",
        button: {
            button_theme: "",
            link: "",
            label: "",
        },
    },
    isColumn: false,
    isFirstModule: false,
    isHero: false,
    isThirdItem: false,
    layout: undefined,
    mediaQuery: undefined,
    scrollToClassName: "",
    useGridWrapper: false,
};

// export default React.memo(FlexibleBlock);
export default FlexibleBlock;
